'use client';

import { useDropzone } from 'react-dropzone';
import { TrashIcon } from '@heroicons/react/24/outline';
import { Label } from '@sapling/ui/components/ui/label';
import type { UploadedFile } from '@/hooks/use-grant-application-upload';
import {
  MAX_FILE_SIZE,
  MAX_TOTAL_UPLOAD_SIZE,
} from '@sapling/constants/grants';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type FileUploadFieldProps = {
  files: UploadedFile[];
  totalSize: number;
  uploading: boolean;
  error: string | null;
  onDrop: (files: File[]) => void;
  onRemove: (uploadId: string) => void;
};

export function FileUploadField({
  files,
  totalSize,
  uploading,
  error,
  onDrop,
  onRemove,
}: FileUploadFieldProps) {
  const hasSpace = totalSize < MAX_TOTAL_UPLOAD_SIZE;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: MAX_FILE_SIZE,
    disabled: uploading || !hasSpace,
  });

  return (
    <div>
      <Label>Bifoga filer</Label>
      <p className="mt-1 text-xs text-neutral-400">
        PDF, JPG eller PNG. Max 5 MB per fil, totalt{' '}
        {formatSize(MAX_TOTAL_UPLOAD_SIZE)}.
      </p>

      {hasSpace && (
        <div
          {...getRootProps()}
          className={`mt-3 cursor-pointer rounded-lg border-2 border-dashed p-6 text-center text-sm transition-colors ${
            isDragActive
              ? 'border-neutral-400 bg-neutral-50'
              : 'border-neutral-200 hover:border-neutral-300'
          } ${uploading ? 'pointer-events-none opacity-50' : ''}`}
        >
          <input {...getInputProps()} />
          {uploading
            ? 'Laddar upp...'
            : isDragActive
              ? 'Släpp filer här'
              : 'Dra filer hit eller klicka för att välja'}
        </div>
      )}

      {files.length > 0 && (
        <>
          <ul className="mt-3 space-y-2">
            {files.map((file) => (
              <li
                key={file.uploadId}
                className="flex items-center justify-between rounded border bg-neutral-50 px-3 py-2 text-sm"
              >
                <span className="truncate">
                  {file.fileName}{' '}
                  <span className="text-neutral-400">
                    ({formatSize(file.sizeBytes)})
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => onRemove(file.uploadId)}
                  className="ml-2 cursor-pointer p-2 text-neutral-400 hover:text-red-600"
                  aria-label={`Ta bort ${file.fileName}`}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-neutral-400">
            {formatSize(totalSize)} / {formatSize(MAX_TOTAL_UPLOAD_SIZE)} använt
          </p>
        </>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
